import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import { getKernel } from '../../utils/api';
import './KernelDashboard.css';

const KernelDashboard = () => {
  const { kernelId } = useParams();
  const navigate = useNavigate();
  const [kernel, setKernel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchKernel = async () => {
      try {
        const response = await getKernel(kernelId);
        setKernel(response.kernel);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchKernel();
  }, [kernelId]);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard__loading">Loading kernel data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="dashboard__error">Error: {error}</div>
        <button onClick={() => navigate('/')} className="dashboard__back-btn">
          Back to Home
        </button>
      </div>
    );
  }

  if (!kernel) {
    return (
      <div className="dashboard">
        <div className="dashboard__error">Kernel not found</div>
        <button onClick={() => navigate('/')} className="dashboard__back-btn">
          Back to Home
        </button>
      </div>
    );
  }

  // Prepare chart data
  const chartData = kernel.price_history?.map((point) => ({
    timestamp: new Date(point.timestamp).getTime(),
    timeLabel: new Date(point.timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    price: kernel.selected_market?.type === 'polymarket'
      ? Number(point.price.toFixed(4))
      : Number(point.price.toFixed(2)),
    rawPrice: point.price,
  })) || [];

  const projection = kernel.projection;

  // Get pricing data from new projection structure
  const publicPricing = projection?.publicPricing;
  const privatePricing = projection?.privatePricing;

  // Calculate midpoints
  const publicMidpoint = publicPricing
    ? (Number(publicPricing.fairValueLow) + Number(publicPricing.fairValueHigh)) / 2
    : null;
  const privateMidpoint = privatePricing
    ? (Number(privatePricing.fairValueLow) + Number(privatePricing.fairValueHigh)) / 2
    : null;

  // Get latest market price (M)
  const latestMarketPrice = chartData.length > 0 ? chartData[chartData.length - 1].rawPrice : null;
  const lastTimestamp = chartData.length > 0 ? chartData[chartData.length - 1].timestamp : null;

  // Calculate price of information values
  // |A - M| = difference between private projection and market price
  // |A - B| = difference between private projection and public projection
  const priceVsMarket = (privateMidpoint !== null && latestMarketPrice !== null)
    ? Math.abs(privateMidpoint - latestMarketPrice)
    : null;
  const priceVsPublic = (privateMidpoint !== null && publicMidpoint !== null)
    ? Math.abs(privateMidpoint - publicMidpoint)
    : null;

  // Chart domain calculations
  const dataTimestamps = chartData.map(point => point.timestamp);
  const dataPrices = chartData.map(point => point.rawPrice);
  const dataMinTs = dataTimestamps.length > 0 ? Math.min(...dataTimestamps) : null;
  const dataMaxTs = dataTimestamps.length > 0 ? Math.max(...dataTimestamps) : null;

  // Include forecast dots in Y domain
  const allPrices = [...dataPrices];
  if (publicMidpoint !== null) allPrices.push(publicMidpoint);
  if (privateMidpoint !== null) allPrices.push(privateMidpoint);

  const yDomainMin = allPrices.length > 0 ? Math.min(...allPrices) * 0.95 : 0;
  const yDomainMax = allPrices.length > 0 ? Math.max(...allPrices) * 1.05 : 1;

  const isAccepted = kernel.public_agent_decision === 'Yes';
  const isRejected = !isAccepted;

  // Clean up Polymarket URLs - remove "-XYZ" suffix
  const cleanMarketUrl = kernel.selected_market?.url
    ? kernel.selected_market.url.replace(/-\d{3}$/, '')
    : null;

  // Get projection direction for conditional button display
  const projectionDirection = projection?.direction;
  const showBuyButton = projectionDirection === 'up';
  const showSellButton = projectionDirection === 'down';

  return (
    <div className="dashboard">
      <header className="dashboard__header">
        <button onClick={() => navigate('/')} className="dashboard__back-btn">
          ← New Submission
        </button>
        <div className="dashboard__title-group">
          <img src="/logo.png" alt="CONDITIONAL" className="dashboard__logo" />
          <h1 className="dashboard__title">Trading Insight</h1>
        </div>
        <span className="dashboard__id">{kernelId}</span>
      </header>

      {/* Decision Banner - Only show if rejected */}
      {isRejected && (
        <div className="dashboard__decision dashboard__decision--rejected">
          <span className="dashboard__decision-icon">✗</span>
          <span className="dashboard__decision-text">
            Information Not Accepted
          </span>
        </div>
      )}

      {/* Main Grid */}
      <div className="dashboard__grid">
        {/* Chart and Projection Side-by-Side */}
        {chartData.length > 0 && (
          <div className="dashboard__chart-projection-row">
            {/* Price Chart - Left Half */}
            <section className="dashboard__section dashboard__section--chart">
              {kernel.selected_market && cleanMarketUrl ? (
                <a
                  href={cleanMarketUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dashboard__section-title dashboard__section-title--link"
                >
                  {kernel.selected_market.name}
                </a>
              ) : (
                <h2 className="dashboard__section-title">Price History</h2>
              )}
              <div className="dashboard__chart-legend">
                <div className="dashboard__legend-item">
                  <span className="dashboard__legend-color dashboard__legend-color--history"></span>
                  <span className="dashboard__legend-label">Price History</span>
                </div>
                {publicMidpoint !== null && (
                  <div className="dashboard__legend-item">
                    <span className="dashboard__legend-color dashboard__legend-color--public"></span>
                    <span className="dashboard__legend-label">Public Forecast</span>
                  </div>
                )}
                {privateMidpoint !== null && (
                  <div className="dashboard__legend-item">
                    <span className="dashboard__legend-color dashboard__legend-color--private"></span>
                    <span className="dashboard__legend-label">With Information</span>
                  </div>
                )}
              </div>
              <div className="dashboard__chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                    <XAxis
                      dataKey="timestamp"
                      type="number"
                      scale="time"
                      domain={[dataMinTs, dataMaxTs]}
                      stroke="#888"
                      tick={{ fontSize: 10 }}
                      interval="preserveStartEnd"
                      tickFormatter={(value) => new Date(value).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    />
                    <YAxis
                      stroke="#888"
                      domain={[yDomainMin, yDomainMax]}
                      tickFormatter={(value) => {
                        return kernel.selected_market?.type === 'polymarket'
                          ? Number(value).toFixed(2)
                          : `$${Number(value).toFixed(2)}`;
                      }}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5' }}
                      labelStyle={{ color: '#666' }}
                      formatter={(value) => (
                        kernel.selected_market?.type === 'polymarket'
                          ? Number(value).toFixed(2)
                          : `$${Number(value).toFixed(2)}`
                      )}
                      labelFormatter={(label) => new Date(label).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    />
                    <Line
                      type="monotone"
                      dataKey="price"
                      stroke="#B55C4A"
                      strokeWidth={2}
                      dot={false}
                    />
                    {/* Public forecast dot (without information) */}
                    {publicMidpoint !== null && lastTimestamp !== null && (
                      <ReferenceDot
                        x={lastTimestamp}
                        y={publicMidpoint}
                        r={8}
                        fill="#00308f"
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    )}
                    {/* Private forecast dot (with information) */}
                    {privateMidpoint !== null && lastTimestamp !== null && (
                      <ReferenceDot
                        x={lastTimestamp}
                        y={privateMidpoint}
                        r={8}
                        fill="#B55C4A"
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>

            {/* Market Projection - Right Half */}
            {projection && (
              <section className="dashboard__section dashboard__section--projection">
                <h2 className="dashboard__section-title">Market Projection</h2>
                <div className="dashboard__projection-grid">
                  {/* Box 1: Expected Price Given Information */}
                  <div className="dashboard__projection-metric">
                    <span className="dashboard__metric-label">Expected Price</span>
                    <span className="dashboard__metric-value">
                      {privateMidpoint !== null
                        ? `${(privateMidpoint * 100).toFixed(1)}%`
                        : 'N/A'}
                    </span>
                  </div>

                  {/* Box 2: Price of Information */}
                  <div className="dashboard__projection-metric">
                    <span className="dashboard__metric-label">Information Value</span>
                    <span className="dashboard__metric-value dashboard__metric-value--small">
                      {priceVsMarket !== null && priceVsPublic !== null
                        ? `${(priceVsMarket * 100).toFixed(1)}%, ${(priceVsPublic * 100).toFixed(1)}%`
                        : 'N/A'}
                    </span>
                  </div>

                  {/* Box 3: Direction */}
                  <div className={`dashboard__projection-metric dashboard__projection-metric--${projection.direction}`}>
                    <span className="dashboard__metric-label">Direction</span>
                    <span className="dashboard__metric-value">
                      {projection.direction === 'up' ? '↑' : projection.direction === 'down' ? '↓' : '→'}
                      {' '}{projection.direction?.toUpperCase()}
                    </span>
                  </div>

                  {/* Box 4: Confidence */}
                  <div className={`dashboard__projection-metric dashboard__projection-metric--confidence-${projection.confidence}`}>
                    <span className="dashboard__metric-label">Confidence</span>
                    <span className="dashboard__metric-value">{projection.confidence?.toUpperCase()}</span>
                  </div>
                </div>
                <p className="dashboard__projection-summary">{projection.summary}</p>
                <div className="dashboard__projection-reasoning">
                  <h3>Reasoning</h3>
                  <p>{projection.reasoning}</p>
                </div>
              </section>
            )}
          </div>
        )}

        {/* Trade Actions - Full Width Row */}
        {projection && (
          <section className="dashboard__section dashboard__section--trade">
            <h3 className="dashboard__trade-title">Trade Actions</h3>
            <div className="dashboard__trade-form">
              <div className="dashboard__trade-button-group">
                {showBuyButton && (
                  <button className="dashboard__trade-btn dashboard__trade-btn--buy">
                    BUY {kernel.selected_market?.type === 'polymarket' ? 'YES' : 'LONG'}
                  </button>
                )}
                {showSellButton && (
                  <button className="dashboard__trade-btn dashboard__trade-btn--sell">
                    {kernel.selected_market?.type === 'polymarket' ? 'BUY NO' : 'SHORT'}
                  </button>
                )}
              </div>
              <div className="dashboard__trade-inputs">
                <div className="dashboard__trade-input-group">
                  <label className="dashboard__trade-label">Shares</label>
                  <input
                    type="number"
                    className="dashboard__trade-input"
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div className="dashboard__trade-input-group">
                  <label className="dashboard__trade-label">Lower Risk Limit</label>
                  <input
                    type="number"
                    className="dashboard__trade-input"
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div className="dashboard__trade-input-group">
                  <label className="dashboard__trade-label">Upper Sell Minimum</label>
                  <input
                    type="number"
                    className="dashboard__trade-input"
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>
            </div>
            <p className="dashboard__trade-disclaimer">
              Mock trading only - no real transactions
            </p>
          </section>
        )}

        {/* Belief Text */}
        <section className="dashboard__section">
          <h2 className="dashboard__section-title">Submitted Belief</h2>
          <p className="dashboard__belief-text">{kernel.kernel_text}</p>
        </section>

        {/* Agent Responses */}
        <section className="dashboard__section">
          <h2 className="dashboard__section-title">Agent Analysis</h2>

          <div className="dashboard__agent-response">
            <h3>Initial Assessment</h3>
            <p>{kernel.intake_agent_thoughts || 'N/A'}</p>
          </div>

          <div className="dashboard__agent-response">
            <h3>Search Question</h3>
            <p>{kernel.search_question || 'N/A'}</p>
          </div>

          <div className="dashboard__agent-response">
            <h3>Public Agent Analysis</h3>
            <p>{kernel.public_agent_thoughts || 'N/A'}</p>
          </div>
        </section>

        {/* Aggregation Questions */}
        {kernel.aggregation_questions && kernel.aggregation_questions.length > 0 && (
          <section className="dashboard__section">
            <h2 className="dashboard__section-title">Research Questions</h2>
            <div className="dashboard__questions">
              {kernel.aggregation_questions.map((qa, index) => (
                <div key={index} className="dashboard__question-item">
                  <p className="dashboard__question">Q: {qa.question}</p>
                  <p className="dashboard__answer">A: {qa.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Provider Details */}
        <section className="dashboard__section dashboard__section--provider">
          <h2 className="dashboard__section-title">Provider Details</h2>
          <div className="dashboard__provider-grid">
            <div>
              <span className="dashboard__provider-label">Name</span>
              <span className="dashboard__provider-value">{kernel.provider_details?.name}</span>
            </div>
            <div>
              <span className="dashboard__provider-label">Background</span>
              <span className="dashboard__provider-value">{kernel.provider_details?.background}</span>
            </div>
          </div>
          <div className="dashboard__provider-experience">
            <span className="dashboard__provider-label">Experience</span>
            <p className="dashboard__provider-value">{kernel.provider_details?.experience}</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default KernelDashboard;
